// Exámenes de la ficha 360° (submenú reliv): cuatro pantallas sobre
// medical.examen_registro (+ examen_archivo para adjuntos ≤ 5 MB):
//  - Laboratorio  → "Análisis de Laboratorio": resultados/análisis + archivos.
//  - Estudios Imagen → "Imágenes/PDF": tabla con tipo de examen y descripción.
//  - Por Especialidad → "Exámenes Físicos y Complementarios": secciones por
//    especialidad (Cardiología exacta de reliv) + Audiometría Tonal (Otorrino).
//  - Patología → pedido numerado con estudio/CIE/muestra + informe macro/micro;
//    editable solo 24h (regla reliv); imprime Hoja Normal y Hoja MSP.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, Download, ExternalLink, Eye, Loader2, Paperclip, Pencil, Plus, Printer, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { PanelIntegrado, useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AutoTextarea, Campo, fieldCls, medRpc } from './shared';
import type { Diagnostico, MedicoRow } from './shared';
import { DiagnosticosCie } from './EnfermedadActual';
import { LOCALE } from '@/lib/utils/regional';

export interface ExamenRow {
  id: string; categoria: string; numero: number | null; fecha: string;
  datos: Record<string, unknown>; created_at: string; medico: string | null; n_archivos: number;
}
interface ArchivoRow { id: string; nombre: string; mime: string | null; archivo: string; created_at: string }
export interface Staged { nombre: string; mime: string; archivo: string }

export type MisionEx = { codigo: string | null; nombre: string } | null;
// Snippet HTML de la misión (centro de costo) para los impresos de exámenes:
// se muestra junto al nombre del paciente, recuadrada y en tamaño distintivo.
export const misionInline = (m: MisionEx): string => {
  const nom = (m?.nombre ?? '').trim();
  if (!nom) return '';
  const e = (v: unknown) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
  return `<span style="display:inline-block;border:2px solid #000;border-radius:4px;padding:0 6px;margin-left:8px;font-weight:800;text-transform:uppercase;font-size:11px;white-space:nowrap;vertical-align:middle;"><span style="font-size:8px;font-weight:600;opacity:.7;">MISIÓN </span>${e(m!.codigo ? m!.codigo + ' · ' : '')}${e(nom)}</span>`;
};

const MAX_ARCHIVO = 5 * 1024 * 1024;
const HORAS_EDICION = 24;
export const editable24h = (r: ExamenRow) => Date.now() - new Date(r.created_at).getTime() < HORAS_EDICION * 3600e3;

export const fmtF = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
};
export const fmtFH = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(LOCALE);
};
export const ds = (d: Record<string, unknown>, k: string): string => (typeof d[k] === 'string' ? (d[k] as string) : '');

export function useExamenes(pacienteId: string, categoria: string) {
  return useQuery<ExamenRow[]>({
    queryKey: ['med-ficha', pacienteId, 'examenes', categoria],
    queryFn: () => medRpc<ExamenRow[]>('med_examenes_ficha', { _paciente_id: pacienteId, _categoria: categoria }),
  });
}

export const leerArchivo = (file: File): Promise<Staged> => new Promise((res, rej) => {
  if (file.size > MAX_ARCHIVO) { rej(new Error(`"${file.name}" supera 5 MB`)); return; }
  const r = new FileReader();
  r.onload = () => res({ nombre: file.name, mime: file.type || 'application/octet-stream', archivo: String(r.result) });
  r.onerror = () => rej(new Error(`No se pudo leer "${file.name}"`));
  r.readAsDataURL(file);
});

// Visor de un adjunto (imagen o PDF) dentro de la ficha, sin ventana modal. Los navegadores
// bloquean abrir un enlace «data:» en una pestaña; se convierte a un enlace interno (blob:).
type Visible = { nombre: string; mime: string | null; archivo: string };
function blobDesdeDataUrl(dataUrl: string): Blob {
  const [cab, b64] = dataUrl.split(',', 2);
  const mime = /data:([^;]+)/.exec(cab)?.[1] ?? 'application/octet-stream';
  const bin = atob(b64 ?? '');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
/** Abre un adjunto (data: o URL) en una pestaña nueva de forma segura. */
export function abrirAdjunto(archivo: string) {
  if (!archivo.startsWith('data:')) { window.open(archivo, '_blank', 'noopener'); return; }
  const u = URL.createObjectURL(blobDesdeDataUrl(archivo));
  window.open(u, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(u), 60_000);
}
export function VisorArchivo({ a, onClose }: { a: Visible; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  // al abrir, llevar el visor completo a la vista (la imagen quedaba bajo el borde de la pantalla)
  const caja = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!url) return;
    const t = setTimeout(() => caja.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    return () => clearTimeout(t);
  }, [url]);
  useEffect(() => {
    let u: string | null = null;
    if (!a.archivo.startsWith('data:')) { setUrl(a.archivo); return undefined; }   // ya es una URL normal
    try { u = URL.createObjectURL(blobDesdeDataUrl(a.archivo)); setUrl(u); } catch { setUrl(null); }
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [a.archivo]);
  const mime = a.mime || (/\.pdf($|\?)/i.test(a.archivo) ? 'application/pdf' : /\.(png|jpe?g|gif|webp)($|\?)/i.test(a.archivo) ? 'image/*' : (/data:([^;]+)/.exec(a.archivo)?.[1] ?? ''));
  return (
    <div ref={caja} className="scroll-mt-20">
    <PanelIntegrado title={a.nombre} onClose={onClose} wide>
      {!url ? <p className="text-sm text-red-600">No se pudo leer el archivo.</p>
        : mime.startsWith('image/') ? <img src={url} alt={a.nombre} className="mx-auto max-h-[65vh] rounded border object-contain" />
        : mime === 'application/pdf' ? <iframe src={url} title={a.nombre} className="h-[75vh] w-full rounded border" />
        : <p className="text-sm text-muted-foreground">Este tipo de archivo no se puede mostrar aquí: ábralo en otra pestaña o descárguelo.</p>}
      {url && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => window.open(url, '_blank', 'noopener')}><ExternalLink className="mr-1 h-3.5 w-3.5" /> Abrir en pestaña nueva</Button>
          <a href={url} download={a.nombre}><Button size="sm" variant="outline"><Download className="mr-1 h-3.5 w-3.5" /> Descargar</Button></a>
          <Button size="sm" onClick={onClose}>Cerrar</Button>
        </div>
      )}
    </PanelIntegrado>
    </div>
  );
}

// Zona de carga (staged: los archivos se suben al guardar el registro)
export function Dropzone({ staged, setStaged }: { staged: Staged[]; setStaged: (f: (s: Staged[]) => Staged[]) => void }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [viendo, setViendo] = useState<Staged | null>(null);
  const agregar = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach((f) => {
      leerArchivo(f).then((a) => setStaged((s) => [...s, a])).catch((e) => toast.error(e.message));
    });
  };
  return (
    <div>
      <div role="button" tabIndex={0}
        className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-5 text-sm text-muted-foreground hover:bg-muted"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter') inputRef.current?.click(); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); agregar(e.dataTransfer.files); }}>
        <Upload className="w-4 h-4" /> Arrastra o da un click aquí para subir archivos (máx. 5 MB c/u)
      </div>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => { agregar(e.target.files); e.target.value = ''; }} />
      {staged.length > 0 && (
        <ul className="mt-1 divide-y rounded-md border text-sm">
          {staged.map((a, i) => (
            <li key={i} className="flex items-center justify-between gap-2 px-3 py-1.5">
              <span className="flex min-w-0 items-center gap-2">
                {a.mime.startsWith('image/')
                  ? <img src={a.archivo} alt={a.nombre} className="h-10 w-10 shrink-0 cursor-pointer rounded border object-cover" onClick={() => setViendo(a)} />
                  : <Paperclip className="w-4 h-4 shrink-0 text-muted-foreground" />}
                <span className="truncate">{a.nombre}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">(por guardar)</span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <button type="button" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" onClick={() => setViendo(a)}><Eye className="w-3.5 h-3.5" /> Ver</button>
                <button type="button" className="text-red-500" title="Quitar" aria-label="Quitar" onClick={() => setStaged((s) => s.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {viendo && <div className="mt-2"><VisorArchivo a={viendo} onClose={() => setViendo(null)} /></div>}
    </div>
  );
}

// Archivos ya guardados de un examen (ver / eliminar)
export function ArchivosGuardados({ examenId, pacienteId, editable, abrirPrimero = false }: { examenId: string; pacienteId: string; editable: boolean; abrirPrimero?: boolean }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [viendo, setViendo] = useState<ArchivoRow | null>(null);
  const descargar = (a: ArchivoRow) => {
    const u = URL.createObjectURL(blobDesdeDataUrl(a.archivo));
    const el = document.createElement('a'); el.href = u; el.download = a.nombre; el.click();
    setTimeout(() => URL.revokeObjectURL(u), 2000);
  };
  const { data: rows = [] } = useQuery<ArchivoRow[]>({
    queryKey: ['med-ficha', pacienteId, 'examen-archivos', examenId],
    queryFn: () => medRpc<ArchivoRow[]>('med_examen_archivos', { _examen_id: examenId }),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => medRpc('med_examen_archivo_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Archivo eliminado'); void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  // «Ver» desde la columna Acción: abre directamente el primer archivo
  const abiertoInicial = useRef(false);
  useEffect(() => {
    if (abrirPrimero && !abiertoInicial.current && rows.length > 0) { abiertoInicial.current = true; setViendo(rows[0]); }
  }, [abrirPrimero, rows]);
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">Sin archivos adjuntos</p>;
  return (
    <>
      {dlg.ui}
      <ul className="divide-y rounded-md border text-sm">
      {rows.map((a) => (
        <li key={a.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
          <span className="flex min-w-0 items-center gap-2">
            {a.mime?.startsWith('image/')
              ? <img src={a.archivo} alt={a.nombre} className="h-10 w-10 cursor-pointer rounded border object-cover" onClick={() => setViendo(a)} />
              : <Paperclip className="w-4 h-4 shrink-0 text-muted-foreground" />}
            <span className="truncate">{a.nombre}</span>
          </span>
          <span className="flex shrink-0 items-center gap-3">
            <button type="button" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" onClick={() => setViendo(viendo?.id === a.id ? null : a)}><Eye className="w-3.5 h-3.5" /> Ver</button>
            <button type="button" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" onClick={() => descargar(a)}><Download className="w-3.5 h-3.5" /> Descargar</button>
            {editable && (
              <button type="button" className="text-red-500" title="Eliminar" aria-label="Eliminar"
                onClick={async () => { if (!(await dlg.confirmar(`¿Eliminar "${a.nombre}"?`, { peligro: true }))) return; eliminar.mutate(a.id); }}><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </span>
        </li>
      ))}
      </ul>
      {viendo && <div className="mt-2"><VisorArchivo a={viendo} onClose={() => setViendo(null)} /></div>}
    </>
  );
}

export function useGuardarExamen(pacienteId: string, categoria: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, fecha, medicoId, datos, staged }: {
      id?: string; fecha?: string; medicoId?: string; datos: Record<string, unknown>; staged: Staged[];
    }) => {
      const r = await medRpc<{ id: string }>('med_examen_guardar', {
        _data: { id, paciente_id: pacienteId, categoria, fecha: fecha || undefined, medico_id: medicoId || null, datos },
      });
      for (const a of staged) {
        await medRpc('med_examen_archivo_agregar', { _examen_id: r.id, _nombre: a.nombre, _mime: a.mime, _archivo: a.archivo });
      }
      return r;
    },
    onSuccess: () => { toast.success('Guardado'); void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useEliminarExamen(pacienteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => medRpc('med_examen_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Eliminado'); void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function CabeceraEx({ titulo, onVolver }: { titulo: string; onVolver: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={onVolver}><ArrowLeft className="w-4 h-4 mr-1" /> Volver a la ficha</Button>
      <h2 className="text-lg font-semibold">{titulo}</h2>
    </div>
  );
}

export function BarraEx({ titulo, accion }: { titulo: string; accion?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between rounded-t-md bg-primary/10 px-4 py-2">
      <span className="text-sm font-semibold">{titulo}</span>
      {accion}
    </div>
  );
}

export function CampoVer({ label, v }: { label: string; v?: string | null }) {
  if (!v?.trim()) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase text-muted-foreground">{label}</h4>
      <p className="mt-1 whitespace-pre-wrap rounded-md border px-3 py-2 text-sm">{v}</p>
    </div>
  );
}

export function Historial({ rows, selId, onSel, etiqueta }: {
  rows: ExamenRow[]; selId: string | null; onSel: (id: string) => void; etiqueta: (r: ExamenRow) => string;
}) {
  return (
    <div className="w-48 shrink-0 rounded-md border">
      <div className="border-b bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground">Historial</div>
      <div className="max-h-80 overflow-y-auto">
        {rows.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground">Sin registros</p>}
        {rows.map((r) => (
          <button key={r.id} type="button" onClick={() => onSel(r.id)}
            className={`block w-full border-b px-3 py-1.5 text-left text-sm last:border-0 hover:bg-muted ${selId === r.id ? 'bg-primary/10 font-medium' : ''}`}>
            {etiqueta(r)}
            {r.n_archivos > 0 && <Paperclip className="inline w-3 h-3 ml-1 text-muted-foreground" />}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1) Laboratorio — "Análisis de Laboratorio"
// ---------------------------------------------------------------------------
export function ExamenesLaboratorio({ pacienteId, onVolver }: { pacienteId: string; onVolver: () => void }) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'laboratorio');
  const guardar = useGuardarExamen(pacienteId, 'laboratorio');
  const eliminar = useEliminarExamen(pacienteId);
  const dlg = useDialogoEnLinea();
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<{ id?: string; resultados: string; analisis: string; staged: Staged[] } | null>(null);
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Exámenes — Laboratorio" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Análisis de Laboratorio"
          accion={!form && <Button size="sm" onClick={() => setForm({ resultados: '', analisis: '', staged: [] })}><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4">
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }} etiqueta={(r) => fmtF(r.fecha)} />
            <div className="flex-1 min-w-0 space-y-3">
              {form ? (<>
                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">Resultados:</h4>
                  <AutoTextarea className="mt-1" value={form.resultados} onChange={(e) => setForm({ ...form, resultados: e.target.value })} />
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">Análisis:</h4>
                  <AutoTextarea className="mt-1" value={form.analisis} onChange={(e) => setForm({ ...form, analisis: e.target.value })} />
                </div>
                {form.id && <ArchivosGuardados examenId={form.id} pacienteId={pacienteId} editable />}
                <Dropzone staged={form.staged} setStaged={(f) => setForm((x) => (x ? { ...x, staged: f(x.staged) } : x))} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" isLoading={guardar.isPending}
                    onClick={() => guardar.mutate({ id: form.id, datos: { resultados: form.resultados, analisis: form.analisis }, staged: form.staged }, { onSuccess: () => setForm(null) })}>Guardar</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existe historial de Análisis de Laboratorio</p>
              ) : (<>
                <p className="text-xs text-muted-foreground">Análisis del {fmtFH(sel.fecha)}</p>
                <CampoVer label="Resultados:" v={ds(sel.datos, 'resultados')} />
                <CampoVer label="Análisis:" v={ds(sel.datos, 'analisis')} />
                <ArchivosGuardados examenId={sel.id} pacienteId={pacienteId} editable={false} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => setForm({ id: sel.id, resultados: ds(sel.datos, 'resultados'), analisis: ds(sel.datos, 'analisis'), staged: [] })}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este análisis?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                  <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2) Estudios Imagen — "Imágenes/PDF"
// ---------------------------------------------------------------------------
const TIPOS_IMAGEN = ['Radiografias', 'Tomografias', 'Resonancia Magnética', 'Ecografía/Ultrasonido', 'Gammagrafía', 'Mamografía', 'Densitometría', 'Otros'];

export function ExamenesImagen({ pacienteId, onVolver }: { pacienteId: string; onVolver: () => void }) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'imagen');
  const guardar = useGuardarExamen(pacienteId, 'imagen');
  const eliminar = useEliminarExamen(pacienteId);
  const dlg = useDialogoEnLinea();
  const [form, setForm] = useState<{ id?: string; tipo: string; descripcion: string; staged: Staged[] } | null>(null);
  const [verId, setVerId] = useState<string | null>(null);
  const [verDirecto, setVerDirecto] = useState(false);

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Exámenes — Estudios de Imagen" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Imágenes/PDF"
          accion={!form && <Button size="sm" onClick={() => setForm({ tipo: '', descripcion: '', staged: [] })}><Plus className="w-4 h-4 mr-1" /> Nueva Imagen/PDF</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4 space-y-3">
          {form && (
            <div className="space-y-3 rounded-md border p-3">
              <Dropzone staged={form.staged} setStaged={(f) => setForm((x) => (x ? { ...x, staged: f(x.staged) } : x))} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Campo label={form.id ? 'Editar Examen:' : 'Tipo Examen:'}>
                  <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                    <option value="">Seleccione..</option>{TIPOS_IMAGEN.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Campo>
                <Campo label={form.id ? 'Editar Descripción:' : 'Descripción:'}>
                  <AutoTextarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
                </Campo>
              </div>
              {form.id && <ArchivosGuardados examenId={form.id} pacienteId={pacienteId} editable />}
              <div className="flex gap-2">
                <Button size="sm" isLoading={guardar.isPending} disabled={!form.tipo}
                  onClick={() => guardar.mutate({ id: form.id, datos: { tipo_examen: form.tipo, descripcion: form.descripcion }, staged: form.staged }, { onSuccess: () => setForm(null) })}>Guardar</Button>
                <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
              </div>
            </div>
          )}

          {rows.length === 0 && !form ? (
            <p className="py-6 text-center text-sm text-primary/80">No existen imágenes/PDFs.</p>
          ) : rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
                  <th className="py-2 px-2 text-left">Fecha</th><th className="py-2 px-2 text-left">Tipo Examen</th>
                  <th className="py-2 px-2 text-left">Descripción</th><th className="py-2 px-2 text-center">Archivos</th>
                  <th className="py-2 px-2 text-right">Acción</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (<Fragment key={r.id}>
                    <tr className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 px-2 whitespace-nowrap">{fmtFH(r.fecha)}</td>
                      <td className="py-2 px-2">{ds(r.datos, 'tipo_examen') || '—'}</td>
                      <td className="py-2 px-2">{ds(r.datos, 'descripcion') || '—'}</td>
                      <td className="py-2 px-2 text-center">
                        <button type="button" className="text-primary hover:underline text-xs" onClick={() => { setVerDirecto(false); setVerId(verId === r.id ? null : r.id); }}>
                          <Paperclip className="inline w-3.5 h-3.5" /> {r.n_archivos}
                        </button>
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-primary hover:bg-muted disabled:opacity-30" title={r.n_archivos ? 'Ver imagen / PDF' : 'Sin archivos'} aria-label="Ver"
                          disabled={!r.n_archivos} onClick={() => { const abrir = !(verId === r.id && verDirecto); setVerDirecto(abrir); setVerId(abrir ? r.id : null); }}><Eye className="w-4 h-4" /></button>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title="Editar" aria-label="Editar"
                          onClick={() => setForm({ id: r.id, tipo: ds(r.datos, 'tipo_examen'), descripcion: ds(r.datos, 'descripcion'), staged: [] })}><Pencil className="w-4 h-4" /></button>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar"
                          onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este estudio?', { peligro: true }))) return; eliminar.mutate(r.id); }}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                    {verId === r.id && (
                      <tr className="border-b bg-muted/20"><td colSpan={5} className="px-2 py-2">
                        <ArchivosGuardados key={`${r.id}-${verDirecto}`} examenId={r.id} pacienteId={pacienteId} editable={false} abrirPrimero={verDirecto} />
                      </td></tr>
                    )}
                  </Fragment>))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3) Por Especialidad — "Exámenes Físicos y Complementarios"
// ---------------------------------------------------------------------------
const ESPECIALIDADES = ['Cardiología', 'Gastroenterología', 'Ginecología', 'Neumología', 'Neurología', 'Otorrinolaringología', 'Urología', 'Odontología'];
const SECCIONES_GENERICAS: [string, string][] = [
  ['examen_fisico', 'Examen Físico'], ['hallazgos', 'Hallazgos'],
  ['estudios_complementarios', 'Estudios Complementarios'], ['conclusiones', 'Conclusiones'],
];
const SECCIONES_ESP: Record<string, [string, string][]> = {
  'Cardiología': [
    ['electrocardiograma', 'Electrocardiograma'], ['ecocardiograma', 'Ecocardiograma'],
    ['prueba_esfuerzo', 'Prueba de Esfuerzo'], ['mapa', 'MAPA'], ['holter', 'Holter'],
    ['pruebas_imagen', 'Pruebas de Imagen'], ['pruebas_laboratorio', 'Pruebas de Laboratorio'], ['biopsias', 'Biopsias'],
  ],
  'Otorrinolaringología': [
    ['otoscopia', 'Otoscopia'], ['rinoscopia', 'Rinoscopia'], ['laringoscopia', 'Laringoscopia'], ['conclusiones', 'Conclusiones'],
  ],
};
const seccionesDe = (esp: string) => SECCIONES_ESP[esp] ?? SECCIONES_GENERICAS;

// Audiometría Tonal Liminar (Otorrino): 4 filas × 7 frecuencias + PTP auto + %P
const AUDIO_FILAS: [string, string, string][] = [
  ['od_aerea', 'Oído Derecho', 'Vía Aérea'], ['od_osea', 'Oído Derecho', 'Vía Ósea'],
  ['oi_aerea', 'Oído Izquierdo', 'Vía Aérea'], ['oi_osea', 'Oído Izquierdo', 'Vía Ósea'],
];
const AUDIO_FREQ = ['250', '500', '1000', '2000', '3000', '4000', '8000'];
type Audio = Record<string, Record<string, string>>;

const ptp = (fila: Record<string, string> | undefined) => {
  const vals = ['500', '1000', '2000', '4000'].map((f) => Number(fila?.[`f${f}`])).filter((n) => Number.isFinite(n) && n > 0);
  return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : '';
};

function AudiometriaGrid({ audio, setAudio, soloLectura = false }: { audio: Audio; setAudio?: (f: (a: Audio) => Audio) => void; soloLectura?: boolean }) {
  const set = (fila: string, k: string, v: string) => setAudio?.((a) => ({ ...a, [fila]: { ...(a[fila] ?? {}), [k]: v } }));
  const celda = (fila: string, k: string, w = 'w-14') => soloLectura
    ? <span>{audio[fila]?.[k] || '—'}</span>
    : <input className={`${fieldCls} ${w} px-1 text-center text-xs`} value={audio[fila]?.[k] ?? ''} onChange={(e) => set(fila, k, e.target.value)} />;
  return (
    <div className="overflow-x-auto">
      <h4 className="text-xs font-semibold uppercase text-muted-foreground">Audiometría Tonal Liminar</h4>
      <table className="mt-1 w-full text-xs">
        <thead><tr className="border-b bg-muted text-muted-foreground">
          <th className="py-1.5 px-2 text-left">Oído</th><th className="py-1.5 px-2 text-left">Vía</th>
          <th className="py-1.5 px-2">Enmascarar (S/N)</th>
          {AUDIO_FREQ.map((f) => <th key={f} className="py-1.5 px-2">{f} Hz</th>)}
          <th className="py-1.5 px-2">Prom. Tonos Puros (dB)</th><th className="py-1.5 px-2">% de Pérdida</th>
        </tr></thead>
        <tbody>
          {AUDIO_FILAS.map(([k, oido, via]) => (
            <tr key={k} className="border-b last:border-0">
              <td className="py-1 px-2 font-medium whitespace-nowrap">{oido}</td>
              <td className="py-1 px-2 whitespace-nowrap">{via}</td>
              <td className="py-1 px-2 text-center">{celda(k, 'enmascarar', 'w-10')}</td>
              {AUDIO_FREQ.map((f) => <td key={f} className="py-1 px-2 text-center">{celda(k, `f${f}`)}</td>)}
              <td className="py-1 px-2 text-center font-medium">{ptp(audio[k]) || '—'}</td>
              <td className="py-1 px-2 text-center">{celda(k, 'perdida', 'w-12')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-1 text-[11px] text-muted-foreground">SIN RESPUESTA = 120 · Promedio de tonos puros: 500-4000 Hz (automático)</p>
    </div>
  );
}

export function ExamenesEspecialidad({ pacienteId, nombre, mision, onVolver }: { pacienteId: string; nombre: string; mision?: MisionEx; onVolver: () => void }) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'especialidad');
  const guardar = useGuardarExamen(pacienteId, 'especialidad');
  const eliminar = useEliminarExamen(pacienteId);
  const [selId, setSelId] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const [form, setForm] = useState<{ id?: string; especialidad: string; secciones: Record<string, string>; audio: Audio; staged: Staged[] } | null>(null);
  const dlg = useDialogoEnLinea();
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const abrirNuevo = (esp: string) => {
    setMenu(false);
    setForm({ especialidad: esp, secciones: {}, audio: {}, staged: [] });
  };
  const abrirEditar = (r: ExamenRow) => setForm({
    id: r.id, especialidad: ds(r.datos, 'especialidad') || 'Cardiología',
    secciones: { ...((r.datos.secciones as Record<string, string>) ?? {}) },
    audio: { ...((r.datos.audiometria as Audio) ?? {}) }, staged: [],
  });

  const imprimir = (r: ExamenRow) => {
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
    const esp = ds(r.datos, 'especialidad');
    const secs = (r.datos.secciones as Record<string, string>) ?? {};
    const audio = (r.datos.audiometria as Audio) ?? {};
    const filasSec = seccionesDe(esp).filter(([k]) => secs[k]?.trim())
      .map(([k, l]) => `<h3>${esc(l)}</h3><p>${esc(secs[k])}</p>`).join('');
    const audioHtml = Object.keys(audio).length
      ? `<h3>Audiometría Tonal Liminar</h3><table><thead><tr><th>Oído</th><th>Vía</th><th>Enm.</th>${AUDIO_FREQ.map((f) => `<th>${f} Hz</th>`).join('')}<th>PTP</th><th>%P</th></tr></thead><tbody>${AUDIO_FILAS.map(([k, o, v]) => `<tr><td>${o}</td><td>${v}</td><td class="c">${esc(audio[k]?.enmascarar ?? '')}</td>${AUDIO_FREQ.map((f) => `<td class="c">${esc(audio[k]?.[`f${f}`] ?? '')}</td>`).join('')}<td class="c"><b>${ptp(audio[k]) || ''}</b></td><td class="c">${esc(audio[k]?.perdida ?? '')}</td></tr>`).join('')}</tbody></table><p class="nota">SIN RESPUESTA = 120</p>` : '';
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Examen de ${esc(esp)}</title>
      <style>*{font-family:Arial,Helvetica,sans-serif}h1{font-size:16px;margin:0 0 2px}.sub{font-size:12px;color:#555;margin:0 0 10px}
      h3{font-size:12px;margin:10px 0 2px;text-transform:uppercase;color:#333}p{font-size:12px;margin:0;white-space:pre-wrap}
      table{border-collapse:collapse;width:100%;font-size:10px;margin-top:4px}th,td{border:1px solid #ccc;padding:3px 4px;text-align:left}
      th{background:#f0f0f0}.c{text-align:center}.nota{font-size:10px;color:#555}@media print{@page{margin:12mm}}</style></head><body>
      <h1>Revisión de ${esc(esp)}</h1>
      <p class="sub">Paciente: <b>${esc(nombre)}</b>${misionInline(mision ?? null)} · Fecha: ${esc(fmtFH(r.fecha))}${r.medico ? ` · Médico: ${esc(r.medico)}` : ''}</p>
      ${audioHtml}${filasSec}
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Permite las ventanas emergentes para imprimir'); return; }
    win.document.write(html); win.document.close();
  };

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Exámenes — Por Especialidad" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Exámenes Físicos y Complementarios"
          accion={!form && (
            <div className="relative">
              <Button size="sm" onClick={() => setMenu((o) => !o)}><Plus className="w-4 h-4 mr-1" /> Nuevo Examen <ChevronDown className="w-3.5 h-3.5 ml-1" /></Button>
              {menu && (
                <div className="absolute right-0 z-10 mt-1 w-48 rounded-md border bg-card shadow-lg">
                  {ESPECIALIDADES.map((e) => (
                    <button key={e} type="button" className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted" onClick={() => abrirNuevo(e)}>{e}</button>
                  ))}
                </div>
              )}
            </div>
          )} />
        <div className="rounded-b-md border border-t-0 bg-card p-4">
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }}
              etiqueta={(r) => `${ds(r.datos, 'especialidad') || '—'} · ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3">
              {form ? (<>
                <p className="font-semibold text-sm">Revisión de {form.especialidad}</p>
                {form.especialidad === 'Otorrinolaringología' && (
                  <AudiometriaGrid audio={form.audio} setAudio={(f) => setForm((x) => (x ? { ...x, audio: f(x.audio) } : x))} />
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {seccionesDe(form.especialidad).map(([k, l]) => (
                    <div key={k}>
                      <span className="text-xs font-semibold uppercase text-muted-foreground">{l}</span>
                      <AutoTextarea className="mt-1" value={form.secciones[k] ?? ''}
                        onChange={(e) => setForm((x) => (x ? { ...x, secciones: { ...x.secciones, [k]: e.target.value } } : x))} />
                    </div>
                  ))}
                </div>
                {form.id && <ArchivosGuardados examenId={form.id} pacienteId={pacienteId} editable />}
                <Dropzone staged={form.staged} setStaged={(f) => setForm((x) => (x ? { ...x, staged: f(x.staged) } : x))} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" isLoading={guardar.isPending}
                    onClick={() => guardar.mutate({
                      id: form.id,
                      datos: {
                        especialidad: form.especialidad,
                        secciones: Object.fromEntries(Object.entries(form.secciones).filter(([, v]) => v.trim())),
                        ...(Object.keys(form.audio).length ? { audiometria: form.audio } : {}),
                      },
                      staged: form.staged,
                    }, { onSuccess: () => setForm(null) })}>Guardar</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existen exámenes por especialidad — usa «Nuevo Examen»</p>
              ) : (() => {
                const esp = ds(sel.datos, 'especialidad');
                const secs = (sel.datos.secciones as Record<string, string>) ?? {};
                const audio = (sel.datos.audiometria as Audio) ?? {};
                return (
                  <div className="space-y-3">
                    <p className="font-semibold text-sm">Revisión de {esp} <span className="font-normal text-muted-foreground">· {fmtFH(sel.fecha)}</span></p>
                    {Object.keys(audio).length > 0 && <AudiometriaGrid audio={audio} soloLectura />}
                    {seccionesDe(esp).map(([k, l]) => <CampoVer key={k} label={l} v={secs[k]} />)}
                    <ArchivosGuardados examenId={sel.id} pacienteId={pacienteId} editable={false} />
                    <div className="flex gap-2 border-t pt-3">
                      <Button size="sm" variant="outline" onClick={() => imprimir(sel)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
                      <Button size="sm" variant="outline" onClick={() => abrirEditar(sel)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                      <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este examen?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                      <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4) Patología — pedido numerado + informe (editable 24h)
// ---------------------------------------------------------------------------
interface FormPat {
  id?: string; medicoId: string; estudio: string; estudio_detalle: string; resumen_clinico: string;
  diagnostico: string; dx: Diagnostico[]; muestra: string; tratamiento: string;
  fecha_muestra: string; recepcion_placa: string; fecha_informe: string;
  macroscopico: string; microscopico: string; conclusiones: string; observaciones: string;
}

export function ExamenesPatologia({ pacienteId, nombre, mision, onVolver }: { pacienteId: string; nombre: string; mision?: MisionEx; onVolver: () => void }) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'patologia');
  const { data: medicos = [] } = useQuery<MedicoRow[]>({ queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }) });
  // El médico solicitante es el usuario logueado (no se elige). Admin/recepción mantiene el selector.
  const { data: miMedico } = useQuery<{ id: string; razon_social: string } | null>({
    queryKey: ['med-mi-medico'], queryFn: () => medRpc<{ id: string; razon_social: string } | null>('med_mi_medico'),
  });
  const miMedicoId = miMedico?.id ?? '';
  const guardar = useGuardarExamen(pacienteId, 'patologia');
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<FormPat | null>(null);
  const [printMenu, setPrintMenu] = useState(false);
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;
  const nPedido = useMemo(() => (r: ExamenRow) => r.numero ?? '—', []);

  const vacioPat: FormPat = {
    medicoId: '', estudio: 'Histopatologia', estudio_detalle: '', resumen_clinico: '', diagnostico: '', dx: [],
    muestra: '', tratamiento: '', fecha_muestra: '', recepcion_placa: '', fecha_informe: '',
    macroscopico: '', microscopico: '', conclusiones: '', observaciones: '',
  };
  const abrirEditar = (r: ExamenRow) => {
    const d = r.datos;
    setForm({
      id: r.id, medicoId: ds(d, 'medico_id'), estudio: ds(d, 'estudio') || 'Histopatologia', estudio_detalle: ds(d, 'estudio_detalle'),
      resumen_clinico: ds(d, 'resumen_clinico'), diagnostico: ds(d, 'diagnostico'),
      dx: Array.isArray(d.diagnosticos) ? (d.diagnosticos as Diagnostico[]) : [],
      muestra: ds(d, 'muestra'), tratamiento: ds(d, 'tratamiento'),
      fecha_muestra: ds(d, 'fecha_muestra'), recepcion_placa: ds(d, 'recepcion_placa'), fecha_informe: ds(d, 'fecha_informe'),
      macroscopico: ds(d, 'macroscopico'), microscopico: ds(d, 'microscopico'),
      conclusiones: ds(d, 'conclusiones'), observaciones: ds(d, 'observaciones'),
    });
  };

  const imprimir = (r: ExamenRow, msp: boolean) => {
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
    const d = r.datos;
    const medico = medicos.find((m) => m.id === ds(d, 'medico_id'))?.razon_social ?? r.medico ?? '—';
    const dx = Array.isArray(d.diagnosticos) ? (d.diagnosticos as Diagnostico[]) : [];
    const dxHtml = dx.length ? `<table><thead><tr><th>Código</th><th>CIE-10</th><th class="c">Presuntivo</th><th class="c">Definitivo</th></tr></thead><tbody>${dx.map((x) => `<tr><td>${esc(x.cie10_codigo ?? '—')}</td><td>${esc(x.descripcion)}</td><td class="c">${x.tipo === 'presuntivo' ? '●' : ''}</td><td class="c">${x.tipo === 'definitivo' ? '●' : ''}</td></tr>`).join('')}</tbody></table>` : '';
    const campo = (l: string, v: string) => (v.trim() ? `<h3>${esc(l)}</h3><p>${esc(v)}</p>` : '');
    const cuerpo = `
      <p class="dato"><b>N° DE PEDIDO:</b> ${esc(r.numero ?? '—')} · <b>Médico que solicita:</b> ${esc(medico)} · <b>Fecha:</b> ${esc(fmtF(r.fecha))}</p>
      <p class="dato"><b>Estudio solicitado:</b> ${esc(ds(d, 'estudio'))}${ds(d, 'estudio_detalle') ? ` — ${esc(ds(d, 'estudio_detalle'))}` : ''}</p>
      ${campo('Resumen Clínico', ds(d, 'resumen_clinico'))}${campo('Diagnóstico', ds(d, 'diagnostico'))}${dxHtml}
      ${campo('Muestra o Pieza', ds(d, 'muestra'))}${campo('Tratamiento', ds(d, 'tratamiento'))}
      <h2>FECHAS</h2>
      <p class="dato"><b>Fecha de Muestra:</b> ${esc(fmtF(ds(d, 'fecha_muestra')) )} · <b>Recepción de Placa:</b> ${esc(fmtF(ds(d, 'recepcion_placa')))} · <b>Fecha de Informe:</b> ${esc(fmtF(ds(d, 'fecha_informe')))}</p>
      <h2>INFORME</h2>
      ${campo('Macroscópico', ds(d, 'macroscopico'))}${campo('Microscópico', ds(d, 'microscopico'))}
      ${campo('Conclusiones', ds(d, 'conclusiones'))}${campo('Observaciones', ds(d, 'observaciones'))}`;
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Pedido de patología ${esc(r.numero ?? '')}</title>
      <style>*{font-family:Arial,Helvetica,sans-serif}h1{font-size:${msp ? 13 : 16}px;margin:0 0 2px;${msp ? 'text-align:center;text-transform:uppercase' : ''}}
      h2{font-size:12px;margin:12px 0 2px;border-bottom:1px solid #333}h3{font-size:11px;margin:8px 0 2px;text-transform:uppercase;color:#333}
      .sub{font-size:12px;color:#555;margin:0 0 10px;${msp ? 'text-align:center' : ''}}p{font-size:12px;margin:0;white-space:pre-wrap}.dato{margin:2px 0}
      table{border-collapse:collapse;width:100%;font-size:11px;margin-top:4px}th,td{border:1px solid #333;padding:3px 6px;text-align:left}
      th{background:#eee}.c{text-align:center}${msp ? 'body{border:1.5px solid #333;padding:14px}h3{border-bottom:1px dotted #999}' : ''}
      @media print{@page{margin:12mm}}</style></head><body>
      <h1>${msp ? 'SNS-MSP — SOLICITUD Y REPORTE DE ANATOMÍA PATOLÓGICA' : 'Pedido de Patología'}</h1>
      <p class="sub">Paciente: <b>${esc(nombre)}</b>${misionInline(mision ?? null)}</p>
      ${cuerpo}
      ${msp ? '<br><table><tbody><tr><td style="height:60px;width:50%">FIRMA DEL MÉDICO SOLICITANTE</td><td>FIRMA DEL PATÓLOGO</td></tr></tbody></table>' : ''}
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Permite las ventanas emergentes para imprimir'); return; }
    win.document.write(html); win.document.close();
  };

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  const lbl = 'text-xs font-semibold uppercase text-muted-foreground';
  return (
    <div className="space-y-3">
      <CabeceraEx titulo="Exámenes — Patología" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Patología"
          accion={!form && <Button size="sm" onClick={() => setForm(vacioPat)}><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4 space-y-3">
          <p className="rounded-md bg-primary/10 px-3 py-2 text-xs">
            ℹ️ Estimado Dr/a: le recordamos que los pedidos se podrán modificar únicamente hasta {HORAS_EDICION} horas posterior a su creación.
          </p>
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }}
              etiqueta={(r) => `Pedido ${nPedido(r)} · ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {form ? (<>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Campo label="Médico que Solicita">
                    {miMedicoId ? (
                      <input className={fieldCls} value={miMedico?.razon_social ?? ''} readOnly disabled title="Se toma del médico que ha iniciado sesión" />
                    ) : (
                      <select className={fieldCls} value={form.medicoId} onChange={(e) => setForm({ ...form, medicoId: e.target.value })}>
                        <option value="">— Selecciona —</option>{medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}</option>)}
                      </select>
                    )}
                  </Campo>
                  <div className="sm:col-span-2">
                    <span className={lbl}>Estudio Solicitado</span>
                    <div className="mt-1 flex items-center gap-4">
                      {['Histopatologia', 'Citologia'].map((t) => (
                        <label key={t} className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <input type="radio" className="accent-primary" name="pat-estudio" checked={form.estudio === t} onChange={() => setForm({ ...form, estudio: t })} /> {t === 'Histopatologia' ? 'Histopatología' : 'Citología'}
                        </label>
                      ))}
                    </div>
                    <AutoTextarea className="mt-1" placeholder="Detalle del estudio…" value={form.estudio_detalle} onChange={(e) => setForm({ ...form, estudio_detalle: e.target.value })} />
                  </div>
                </div>
                <div><span className={lbl}>Resumen Clínico</span><AutoTextarea className="mt-1" value={form.resumen_clinico} onChange={(e) => setForm({ ...form, resumen_clinico: e.target.value })} /></div>
                <div><span className={lbl}>Diagnóstico</span><AutoTextarea className="mt-1" value={form.diagnostico} onChange={(e) => setForm({ ...form, diagnostico: e.target.value })} /></div>
                <DiagnosticosCie dx={form.dx} setDx={(dx) => setForm((f) => (f ? { ...f, dx } : f))} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><span className={lbl}>Muestra o Pieza</span><AutoTextarea className="mt-1" value={form.muestra} onChange={(e) => setForm({ ...form, muestra: e.target.value })} /></div>
                  <div><span className={lbl}>Tratamiento</span><AutoTextarea className="mt-1" value={form.tratamiento} onChange={(e) => setForm({ ...form, tratamiento: e.target.value })} /></div>
                </div>
                <h4 className="text-sm font-semibold border-t pt-3">FECHAS</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Campo label="Fecha de Muestra"><input type="date" className={fieldCls} value={form.fecha_muestra} onChange={(e) => setForm({ ...form, fecha_muestra: e.target.value })} /></Campo>
                  <Campo label="Recepción de Placa"><input type="date" className={fieldCls} value={form.recepcion_placa} onChange={(e) => setForm({ ...form, recepcion_placa: e.target.value })} /></Campo>
                  <Campo label="Fecha de Informe"><input type="date" className={fieldCls} value={form.fecha_informe} onChange={(e) => setForm({ ...form, fecha_informe: e.target.value })} /></Campo>
                </div>
                <h4 className="text-sm font-semibold border-t pt-3">INFORME</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><span className={lbl}>Macroscópico</span><AutoTextarea className="mt-1" value={form.macroscopico} onChange={(e) => setForm({ ...form, macroscopico: e.target.value })} /></div>
                  <div><span className={lbl}>Microscópico</span><AutoTextarea className="mt-1" value={form.microscopico} onChange={(e) => setForm({ ...form, microscopico: e.target.value })} /></div>
                  <div><span className={lbl}>Conclusiones</span><AutoTextarea className="mt-1" value={form.conclusiones} onChange={(e) => setForm({ ...form, conclusiones: e.target.value })} /></div>
                  <div><span className={lbl}>Observaciones</span><AutoTextarea className="mt-1" value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></div>
                </div>
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                  <Button size="sm" isLoading={guardar.isPending}
                    onClick={() => {
                      const { id, medicoId: medicoElegido, dx, ...resto } = form;
                      const medicoId = miMedicoId || medicoElegido; // médico logueado manda
                      guardar.mutate({ id, medicoId, datos: { ...resto, medico_id: medicoId, diagnosticos: dx }, staged: [] }, { onSuccess: () => setForm(null) });
                    }}>Guardar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existe historial de pedidos de patología — usa «Nuevo»</p>
              ) : (() => {
                const d = sel.datos;
                const dx = Array.isArray(d.diagnosticos) ? (d.diagnosticos as Diagnostico[]) : [];
                return (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-semibold">NÚMERO DE PEDIDO: {sel.numero ?? '—'} <span className="font-normal text-muted-foreground">· {fmtFH(sel.fecha)}</span></p>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Button size="sm" variant="outline" onClick={() => setPrintMenu((o) => !o)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir <ChevronDown className="w-3.5 h-3.5 ml-1" /></Button>
                          {printMenu && (
                            <div className="absolute right-0 z-10 mt-1 w-36 rounded-md border bg-card shadow-lg">
                              <button type="button" className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted" onClick={() => { setPrintMenu(false); imprimir(sel, false); }}>Hoja Normal</button>
                              <button type="button" className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted" onClick={() => { setPrintMenu(false); imprimir(sel, true); }}>Hoja MSP</button>
                            </div>
                          )}
                        </div>
                        {editable24h(sel) && <Button size="sm" variant="outline" onClick={() => abrirEditar(sel)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>}
                        <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                      </div>
                    </div>
                    <p><b>Médico que solicita:</b> {medicos.find((m) => m.id === ds(d, 'medico_id'))?.razon_social ?? sel.medico ?? '—'}</p>
                    <p><b>Estudio Solicitado:</b> {ds(d, 'estudio') === 'Citologia' ? 'Citología' : 'Histopatología'}{ds(d, 'estudio_detalle') && ` — ${ds(d, 'estudio_detalle')}`}</p>
                    <CampoVer label="Resumen Clínico" v={ds(d, 'resumen_clinico')} />
                    <CampoVer label="Diagnóstico" v={ds(d, 'diagnostico')} />
                    {dx.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
                            <th className="py-1.5 px-2 text-left">Código</th><th className="py-1.5 px-2 text-left">CIE-10</th>
                            <th className="py-1.5 px-2 text-center">Presuntivo</th><th className="py-1.5 px-2 text-center">Definitivo</th>
                          </tr></thead>
                          <tbody>
                            {dx.map((x, i) => (
                              <tr key={i} className="border-b last:border-0">
                                <td className="py-1.5 px-2 font-mono text-primary">{x.cie10_codigo ?? '—'}</td>
                                <td className="py-1.5 px-2 uppercase">{x.descripcion}</td>
                                <td className="py-1.5 px-2 text-center">{x.tipo === 'presuntivo' ? '●' : ''}</td>
                                <td className="py-1.5 px-2 text-center">{x.tipo === 'definitivo' ? '●' : ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <CampoVer label="Muestra o Pieza" v={ds(d, 'muestra')} />
                    <CampoVer label="Tratamiento" v={ds(d, 'tratamiento')} />
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 rounded-md border px-3 py-2 text-xs">
                      <p><b>Fecha de Muestra:</b> {fmtF(ds(d, 'fecha_muestra'))}</p>
                      <p><b>Recepción de Placa:</b> {fmtF(ds(d, 'recepcion_placa'))}</p>
                      <p><b>Fecha de Informe:</b> {fmtF(ds(d, 'fecha_informe'))}</p>
                    </div>
                    <CampoVer label="Macroscópico" v={ds(d, 'macroscopico')} />
                    <CampoVer label="Microscópico" v={ds(d, 'microscopico')} />
                    <CampoVer label="Conclusiones" v={ds(d, 'conclusiones')} />
                    <CampoVer label="Observaciones" v={ds(d, 'observaciones')} />
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5) Formularios — formulario GENÉRICO de aseguradora (reclamación/reembolso)
// ---------------------------------------------------------------------------
const ASEGURADORAS = [
  'BMI', 'Confiamed', 'Humana', 'Salud S.A.', 'Ecuasanitas', 'Panamerican Life', 'Liberty',
  'Sucre', 'Cruz Blanca', 'Bupa', 'Best Doctors', 'Plus Medical', 'Equivida', 'AIG Metropolitana',
  'IESS', 'ISSFA', 'ISSPOL', 'Otra',
];

interface FormAseg {
  id?: string; aseguradora: string; aseguradora_otra: string; poliza: string; titular: string;
  parentesco: string; tipo_paciente: string; medicoId: string; accidente: boolean; accidente_detalle: string;
  preexistente: boolean; motivo_sintomas: string; tiempo_evolucion: string; diagnostico: string;
  dx: Diagnostico[]; tratamiento: string; examenes_solicitados: string; honorarios: string;
  observaciones: string; staged: Staged[];
}

export function FormulariosPaciente({ pacienteId, nombre, identificacion, aseguradora, poliza, onVolver }: {
  pacienteId: string; nombre: string; identificacion?: string | null;
  aseguradora?: string | null; poliza?: string | null; onVolver: () => void;
}) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'formulario');
  const { data: medicos = [] } = useQuery<MedicoRow[]>({ queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }) });
  // El médico tratante es el usuario logueado (no se elige). Si la sesión no es de un médico
  // (admin/recepción), miMedico es null y se mantiene el selector.
  const { data: miMedico } = useQuery<{ id: string; razon_social: string; numero_licencia: string | null } | null>({
    queryKey: ['med-mi-medico'], queryFn: () => medRpc<{ id: string; razon_social: string; numero_licencia: string | null } | null>('med_mi_medico'),
  });
  const miMedicoId = miMedico?.id ?? '';
  const guardar = useGuardarExamen(pacienteId, 'formulario');
  const eliminar = useEliminarExamen(pacienteId);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<FormAseg | null>(null);
  const dlg = useDialogoEnLinea();
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const abrirNuevo = () => {
    const enLista = ASEGURADORAS.includes(aseguradora ?? '');
    setForm({
      aseguradora: aseguradora ? (enLista ? aseguradora : 'Otra') : '', aseguradora_otra: aseguradora && !enLista ? aseguradora : '',
      poliza: poliza ?? '', titular: nombre, parentesco: '', tipo_paciente: 'Titular', medicoId: miMedicoId,
      accidente: false, accidente_detalle: '', preexistente: false, motivo_sintomas: '', tiempo_evolucion: '',
      diagnostico: '', dx: [], tratamiento: '', examenes_solicitados: '', honorarios: '', observaciones: '', staged: [],
    });
  };
  const abrirEditar = (r: ExamenRow) => {
    const d = r.datos;
    setForm({
      id: r.id, aseguradora: ds(d, 'aseguradora'), aseguradora_otra: ds(d, 'aseguradora_otra'),
      poliza: ds(d, 'poliza'), titular: ds(d, 'titular'), parentesco: ds(d, 'parentesco'),
      tipo_paciente: ds(d, 'tipo_paciente') || 'Titular', medicoId: ds(d, 'medico_id') || miMedicoId,
      accidente: d.accidente === true, accidente_detalle: ds(d, 'accidente_detalle'), preexistente: d.preexistente === true,
      motivo_sintomas: ds(d, 'motivo_sintomas'), tiempo_evolucion: ds(d, 'tiempo_evolucion'),
      diagnostico: ds(d, 'diagnostico'), dx: Array.isArray(d.diagnosticos) ? (d.diagnosticos as Diagnostico[]) : [],
      tratamiento: ds(d, 'tratamiento'), examenes_solicitados: ds(d, 'examenes_solicitados'),
      honorarios: ds(d, 'honorarios'), observaciones: ds(d, 'observaciones'), staged: [],
    });
  };
  const asegDe = (r: ExamenRow) => (ds(r.datos, 'aseguradora') === 'Otra' ? ds(r.datos, 'aseguradora_otra') : ds(r.datos, 'aseguradora')) || '—';

  const imprimir = (r: ExamenRow) => {
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
    const d = r.datos;
    const medico = medicos.find((m) => m.id === ds(d, 'medico_id'));
    const dx = Array.isArray(d.diagnosticos) ? (d.diagnosticos as Diagnostico[]) : [];
    const dxHtml = dx.length ? `<table><thead><tr><th>Código CIE-10</th><th>Diagnóstico</th><th class="c">Tipo</th></tr></thead><tbody>${dx.map((x) => `<tr><td>${esc(x.cie10_codigo ?? '—')}</td><td>${esc(x.descripcion)}</td><td class="c">${esc(x.tipo)}</td></tr>`).join('')}</tbody></table>` : '';
    const fila = (l: string, v: string) => `<tr><td class="l">${esc(l)}</td><td>${esc(v || '—')}</td></tr>`;
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Formulario de atención médica</title>
      <style>*{font-family:Arial,Helvetica,sans-serif}body{border:1.5px solid #333;padding:14px}
      h1{font-size:13px;margin:0 0 2px;text-align:center;text-transform:uppercase}.sub{font-size:11px;color:#555;margin:0 0 10px;text-align:center}
      h2{font-size:12px;margin:12px 0 4px;background:#eee;padding:3px 6px;border:1px solid #333}
      table{border-collapse:collapse;width:100%;font-size:11px;margin-top:2px}td,th{border:1px solid #333;padding:3px 6px;vertical-align:top;white-space:pre-wrap}
      td.l{width:200px;font-weight:bold;background:#f7f7f7}th{background:#eee}.c{text-align:center}
      .firmas td{height:60px;vertical-align:bottom;text-align:center;font-size:10px}
      @media print{@page{margin:12mm}}</style></head><body>
      <h1>Formulario de Atención Médica / Solicitud de Reembolso</h1>
      <p class="sub">Aseguradora: <b>${esc(asegDe(r))}</b> · Formulario N° ${esc(r.numero ?? '—')} · Fecha de atención: ${esc(fmtF(r.fecha))}</p>
      <h2>A. Datos del asegurado</h2>
      <table><tbody>
        ${fila('Titular de la póliza', ds(d, 'titular'))}${fila('N° de póliza', ds(d, 'poliza'))}
        ${fila('Paciente', nombre)}${fila('Identificación', identificacion ?? '')}
        ${fila('Tipo de paciente', ds(d, 'tipo_paciente'))}${fila('Parentesco con el titular', ds(d, 'parentesco'))}
      </tbody></table>
      <h2>B. Datos de la atención</h2>
      <table><tbody>
        ${fila('Motivo de consulta / síntomas', ds(d, 'motivo_sintomas'))}
        ${fila('Tiempo de evolución', ds(d, 'tiempo_evolucion'))}
        ${fila('¿Fue accidente?', d.accidente === true ? `SÍ${ds(d, 'accidente_detalle') ? ` — ${ds(d, 'accidente_detalle')}` : ''}` : 'NO')}
        ${fila('¿Enfermedad preexistente/congénita?', d.preexistente === true ? 'SÍ' : 'NO')}
        ${fila('Diagnóstico', ds(d, 'diagnostico'))}
      </tbody></table>
      ${dxHtml}
      <table style="margin-top:6px"><tbody>
        ${fila('Tratamiento prescrito', ds(d, 'tratamiento'))}
        ${fila('Exámenes solicitados', ds(d, 'examenes_solicitados'))}
        ${fila('Honorarios médicos (USD)', ds(d, 'honorarios'))}
        ${fila('Observaciones', ds(d, 'observaciones'))}
      </tbody></table>
      <h2>C. Declaración del médico tratante</h2>
      <table><tbody>
        ${fila('Médico', medico?.razon_social ?? r.medico ?? '')}
        ${fila('Especialidad', medico?.especialidad ?? '')}
      </tbody></table>
      <p style="font-size:10px;margin-top:6px">Declaro que la información consignada en este formulario es verídica y corresponde a la atención brindada al paciente.</p>
      <table class="firmas" style="margin-top:20px"><tbody><tr>
        <td>FIRMA Y SELLO DEL MÉDICO TRATANTE</td><td>FIRMA DEL PACIENTE / TITULAR</td>
      </tr></tbody></table>
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Permite las ventanas emergentes para imprimir'); return; }
    win.document.write(html); win.document.close();
  };

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  const lbl = 'text-xs font-semibold uppercase text-muted-foreground';
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Formularios de aseguradora" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Formularios"
          accion={!form && <Button size="sm" onClick={abrirNuevo}><Plus className="w-4 h-4 mr-1" /> Nuevo formulario</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4">
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }}
              etiqueta={(r) => `${asegDe(r)} · ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {form ? (<>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Campo label="Aseguradora *">
                    <select className={fieldCls} value={form.aseguradora} onChange={(e) => setForm({ ...form, aseguradora: e.target.value })}>
                      <option value="">— Selecciona —</option>{ASEGURADORAS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </Campo>
                  {form.aseguradora === 'Otra' && (
                    <Campo label="Nombre de la aseguradora"><input className={fieldCls} value={form.aseguradora_otra} onChange={(e) => setForm({ ...form, aseguradora_otra: e.target.value })} /></Campo>
                  )}
                  <Campo label="N° de póliza"><input className={fieldCls} value={form.poliza} onChange={(e) => setForm({ ...form, poliza: e.target.value })} /></Campo>
                  <Campo label="Médico tratante *">
                    {miMedicoId ? (
                      <input className={fieldCls} value={miMedico?.razon_social ?? ''} readOnly disabled title="Se toma del médico que ha iniciado sesión" />
                    ) : (
                      <select className={fieldCls} value={form.medicoId} onChange={(e) => setForm({ ...form, medicoId: e.target.value })}>
                        <option value="">— Selecciona —</option>{medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}</option>)}
                      </select>
                    )}
                  </Campo>
                  <Campo label="Titular de la póliza"><input className={fieldCls} value={form.titular} onChange={(e) => setForm({ ...form, titular: e.target.value })} /></Campo>
                  <Campo label="Tipo de paciente">
                    <div className="flex items-center gap-4 pt-2">
                      {['Titular', 'Dependiente'].map((t) => (
                        <label key={t} className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <input type="radio" className="accent-primary" name="form-tipopac" checked={form.tipo_paciente === t} onChange={() => setForm({ ...form, tipo_paciente: t })} /> {t}
                        </label>
                      ))}
                    </div>
                  </Campo>
                  {form.tipo_paciente === 'Dependiente' && (
                    <Campo label="Parentesco con el titular"><input className={fieldCls} value={form.parentesco} onChange={(e) => setForm({ ...form, parentesco: e.target.value })} /></Campo>
                  )}
                </div>
                <div><span className={lbl}>Motivo de consulta / síntomas</span><AutoTextarea className="mt-1" value={form.motivo_sintomas} onChange={(e) => setForm({ ...form, motivo_sintomas: e.target.value })} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                  <Campo label="Tiempo de evolución"><input className={fieldCls} placeholder="p. ej. 3 días" value={form.tiempo_evolucion} onChange={(e) => setForm({ ...form, tiempo_evolucion: e.target.value })} /></Campo>
                  <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
                    <input type="checkbox" className="accent-primary" checked={form.accidente} onChange={(e) => setForm({ ...form, accidente: e.target.checked })} /> ¿Fue accidente?
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
                    <input type="checkbox" className="accent-primary" checked={form.preexistente} onChange={(e) => setForm({ ...form, preexistente: e.target.checked })} /> ¿Preexistente / congénita?
                  </label>
                </div>
                {form.accidente && (
                  <div><span className={lbl}>Detalle del accidente (fecha, lugar, circunstancias)</span><AutoTextarea className="mt-1" value={form.accidente_detalle} onChange={(e) => setForm({ ...form, accidente_detalle: e.target.value })} /></div>
                )}
                <div><span className={lbl}>Diagnóstico</span><AutoTextarea className="mt-1" value={form.diagnostico} onChange={(e) => setForm({ ...form, diagnostico: e.target.value })} /></div>
                <DiagnosticosCie dx={form.dx} setDx={(dx) => setForm((f) => (f ? { ...f, dx } : f))} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><span className={lbl}>Tratamiento prescrito</span><AutoTextarea className="mt-1" value={form.tratamiento} onChange={(e) => setForm({ ...form, tratamiento: e.target.value })} /></div>
                  <div><span className={lbl}>Exámenes solicitados</span><AutoTextarea className="mt-1" value={form.examenes_solicitados} onChange={(e) => setForm({ ...form, examenes_solicitados: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Campo label="Honorarios médicos (USD)"><input type="number" step="0.01" className={fieldCls} value={form.honorarios} onChange={(e) => setForm({ ...form, honorarios: e.target.value })} /></Campo>
                </div>
                <div><span className={lbl}>Observaciones</span><AutoTextarea className="mt-1" value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></div>
                {form.id && <ArchivosGuardados examenId={form.id} pacienteId={pacienteId} editable />}
                <Dropzone staged={form.staged} setStaged={(f) => setForm((x) => (x ? { ...x, staged: f(x.staged) } : x))} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                  <Button size="sm" isLoading={guardar.isPending} disabled={!form.aseguradora || !(miMedicoId || form.medicoId)}
                    onClick={() => {
                      const { id, medicoId: medicoElegido, dx, staged, ...resto } = form;
                      const medicoId = miMedicoId || medicoElegido; // médico logueado manda
                      guardar.mutate({ id, medicoId, datos: { ...resto, medico_id: medicoId, diagnosticos: dx }, staged }, { onSuccess: () => setForm(null) });
                    }}>Guardar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existen formularios — usa «Nuevo formulario»</p>
              ) : (() => {
                const d = sel.datos;
                const dx = Array.isArray(d.diagnosticos) ? (d.diagnosticos as Diagnostico[]) : [];
                return (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-semibold">Formulario N° {sel.numero ?? '—'} — {asegDe(sel)} <span className="font-normal text-muted-foreground">· {fmtF(sel.fecha)}</span></p>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => imprimir(sel)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
                        <Button size="sm" variant="outline" onClick={() => abrirEditar(sel)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                        <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este formulario?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                        <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 rounded-md border px-3 py-2 text-xs">
                      <p><b>Titular:</b> {ds(d, 'titular') || '—'}</p>
                      <p><b>Póliza:</b> {ds(d, 'poliza') || '—'}</p>
                      <p><b>Tipo:</b> {ds(d, 'tipo_paciente') || '—'}{ds(d, 'parentesco') && ` (${ds(d, 'parentesco')})`}</p>
                      <p><b>Médico:</b> {medicos.find((m) => m.id === ds(d, 'medico_id'))?.razon_social ?? sel.medico ?? '—'}</p>
                      <p><b>Accidente:</b> {d.accidente === true ? 'Sí' : 'No'}</p>
                      <p><b>Preexistente:</b> {d.preexistente === true ? 'Sí' : 'No'}</p>
                    </div>
                    <CampoVer label="Motivo de consulta / síntomas" v={ds(d, 'motivo_sintomas')} />
                    <CampoVer label="Tiempo de evolución" v={ds(d, 'tiempo_evolucion')} />
                    <CampoVer label="Detalle del accidente" v={ds(d, 'accidente_detalle')} />
                    <CampoVer label="Diagnóstico" v={ds(d, 'diagnostico')} />
                    {dx.length > 0 && (
                      <p className="text-xs text-muted-foreground">CIE-10: {dx.map((x) => `${x.cie10_codigo ?? ''} ${x.descripcion}`).join(' · ')}</p>
                    )}
                    <CampoVer label="Tratamiento prescrito" v={ds(d, 'tratamiento')} />
                    <CampoVer label="Exámenes solicitados" v={ds(d, 'examenes_solicitados')} />
                    <CampoVer label="Honorarios médicos (USD)" v={ds(d, 'honorarios')} />
                    <CampoVer label="Observaciones" v={ds(d, 'observaciones')} />
                    <ArchivosGuardados examenId={sel.id} pacienteId={pacienteId} editable={false} />
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
